import { useState, useEffect, useCallback } from "react";
import { format } from "date-fns";
import useApi from "./useApi";
import usePage from "./usePage";

const useRepCalls = (repId) => {
  const [calls, setCalls] = useState([]);
  const [callsByDate, setCallsByDate] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { postData } = useApi();
  const { showMessage } = usePage();

  const fetchCalls = useCallback(async () => {
    if (!repId) {
      setCalls([]);
      setCallsByDate({});
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await postData({
        entityName: "Dashboard",
        action: "getCallsForReps",
        data: {
          userId: repId,
          filters: {},
        },
      });

      if (result?.isSuccessful && Array.isArray(result.data)) {
        const enrichedCalls = result.data.map((call) => ({
          ...call,
          // Use yyyymmdd field or scheduleDate, format to yyyy-MM-dd
          scheduledDate:
            call.yyyymmdd ||
            (call.scheduleDate
              ? format(new Date(call.scheduleDate), "yyyy-MM-dd")
              : null),
          // Extract projectId and projectName from nested project object
          projectId: call.project?.id || call.projectId,
          projectName: call.project?.name || call.projectName,
          projectName_fr: call.project?.name_fr || call.projectName_fr,
          // Extract location info
          locationName: call.location?.name || call.locationName,
          locationNumber: call.location?.locationNumber || call.locationNumber,
          locationAddress: call.location?.address || call.locationAddress,
        }));

        setCalls(enrichedCalls);

        // Group calls by date (yyyymmdd format as string "yyyy-MM-dd")
        const grouped = enrichedCalls.reduce((acc, call) => {
          const date = call.scheduledDate || format(new Date(), "yyyy-MM-dd");
          if (!acc[date]) {
            acc[date] = [];
          }
          acc[date].push(call);
          return acc;
        }, {});

        setCallsByDate(grouped);
      } else {
        setError(result?.message || "Failed to fetch calls");
        showMessage(result?.message || "Failed to fetch calls");
      }
    } catch (err) {
      const message = err.message || "Error fetching calls";
      setError(message);
      showMessage(message);
    } finally {
      setIsLoading(false);
    }
  }, [repId, postData, showMessage]);

  const rescheduleCalls = useCallback(
    async (callIds, newDate) => {
      try {
        const result = await postData({
          entityName: "Call",
          action: "bulkReschedule",
          data: {
            callIds,
            newScheduleDate: newDate,
          },
        });

        if (result?.isSuccessful) {
          showMessage("Calls rescheduled successfully");
          // Refresh the calls after rescheduling
          await fetchCalls();
          return true;
        } else {
          showMessage(result?.message || "Failed to reschedule calls");
          return false;
        }
      } catch (err) {
        showMessage("Error rescheduling calls: " + err.message);
        return false;
      }
    },
    [postData, showMessage, fetchCalls],
  );

  const getCallsByDate = useCallback(
    (date) => {
      // Accept both Date objects and string format "yyyy-MM-dd"
      const dateString =
        typeof date === "string" ? date : format(new Date(date), "yyyy-MM-dd");

      return callsByDate[dateString] || [];
    },
    [callsByDate],
  );

  // Fetch calls when repId changes
  useEffect(() => {
    fetchCalls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repId]);

  return {
    calls,
    callsByDate,
    isLoading,
    error,
    fetchCalls,
    rescheduleCalls,
    getCallsByDate,
  };
};

export default useRepCalls;
